import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { CheckCircle } from "@phosphor-icons/react";

export default function MortgagePrequal() {
  const [f, setF] = useState({ monthly_income: 40000, monthly_debts: 5000, down_payment: 500000, name: "", email: "", phone: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/calc/pre-qualification", {
        monthly_income: Number(f.monthly_income),
        monthly_debts: Number(f.monthly_debts),
        down_payment: Number(f.down_payment),
        lead: { name: f.name, email: f.email, phone: f.phone },
      });
      setRes(data);
      toast.success("Preliminary result generated. An advisor will follow up.");
    } catch { toast.error("Please check your details."); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="prequal-page">
      <SEO
        title="Mortgage pre-qualification — Dubai"
        description="Estimate your Dubai mortgage affordability in 30 seconds. Free, indicative, and reviewed by a Point Penta mortgage advisor."
        path="/mortgage-pre-qualification"
      />
      <section className="pt-6 pb-6 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Calculators", path: "/calculators" }, { name: "Pre-qualification", path: "/mortgage-pre-qualification" }]} />
          <div className="overline text-[#001588] mb-4">Mortgage pre-qualification</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-3xl">How much<br /><span className="italic text-neutral-600">can you afford?</span></h1>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12">
          <form onSubmit={submit} className="space-y-4" data-testid="prequal-form">
            <Field label="Monthly household income (AED)" name="monthly_income" f={f} setF={setF} />
            <Field label="Monthly debt repayments (AED)" name="monthly_debts" f={f} setF={setF} />
            <Field label="Available down payment (AED)" name="down_payment" f={f} setF={setF} />
            <div className="border-t border-neutral-200 pt-4">
              <div className="overline text-[#001588] mb-3">Your details</div>
              <Field label="Full name" name="name" f={f} setF={setF} type="text" required />
              <Field label="Email" name="email" f={f} setF={setF} type="email" required />
              <Field label="Phone" name="phone" f={f} setF={setF} type="text" required />
            </div>
            <button type="submit" disabled={busy} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors disabled:opacity-50" data-testid="prequal-submit">
              {busy ? "Calculating…" : "Get pre-qualified"}
            </button>
            <p className="text-xs text-neutral-500">Indicative only, uses UAE Central Bank 50% DBR rule with 4.5% / 25-year illustrative terms.</p>
          </form>
          <div>
            {res ? (
              <div className="border border-[#001588]/40 p-8 bg-[#ffffff] space-y-6" data-testid="prequal-result">
                <div>
                  <div className="overline text-[#001588] mb-2">Maximum affordable property</div>
                  <div className="font-serif text-5xl text-[#001588]">AED {Math.round(res.max_property_value).toLocaleString()}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Stat label="Max monthly EMI" value={`AED ${Math.round(res.max_emi).toLocaleString()}`} />
                  <Stat label="Max loan principal" value={`AED ${Math.round(res.max_loan_principal).toLocaleString()}`} />
                </div>
                <div className="pt-4 border-t border-neutral-200 text-xs text-neutral-500">{res.notes}</div>
                <div className="flex items-center gap-2 text-sm text-[#001588]"><CheckCircle size={16} weight="fill" /> Your details have been shared with our mortgage advisor.</div>
              </div>
            ) : (
              <div className="border border-neutral-200 p-8 text-neutral-500">
                Fill the form to see an instant estimate. Our advisor will follow up within one business day with bank-specific options.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, f, setF, type = "number", required }) {
  return (
    <label className="block">
      <span className="overline text-neutral-500 block mb-2">{label}</span>
      <input
        type={type} required={required} value={f[name] ?? ""}
        onChange={(e) => setF({ ...f, [name]: e.target.value })}
        className="w-full bg-white border border-[#e5e7eb] px-4 py-3 text-[#050d19] focus:outline-none focus:border-[#001588]"
        data-testid={`prequal-${name}`}
      />
    </label>
  );
}
function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
      <div className="font-serif text-2xl text-neutral-900">{value}</div>
    </div>
  );
}
